import {
  Alignment,
  Button,
  Checkbox,
  H6,
  InputGroup,
  Label,
  NumericInput,
  Position,
} from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { t } from 'ttag';
import { useGetProjectAvailableLayersQuery } from 'uf-api-rtk/store/Api';
import { getActiveProjectId } from 'uf/app/selectors';
import { parseFullPath } from 'uf/base/dataset';
import Flex from 'uf/ui/base/Flex';
import Fuse, { FuseResult } from 'fuse.js';
import _ from 'lodash';
import getCategoryLabel from 'uf/projects/layerCategories';
import useUserFlag from 'uf/ui/user/useUserFlag';
import SearchMenu, { FuseOptionKeys, SearchResultItem } from './SearchMenu';
import Icon from '@mdi/react';
import { mdiFilterVariant } from '@mdi/js';
import {
  categoryExpanded,
  clearSearch,
  searchUpdated,
  selectLayer,
  selectLayerManager,
} from 'uf/projects/layerManagerSlice';

const DEFAULT_OPTIONS = {
  includeMatches: true,
  includeScore: true,
  keys: [
    {
      name: FuseOptionKeys.NAME,
      getFn: ({ layer }) => layer?.details?.name,
      weight: 10,
    },
    {
      name: FuseOptionKeys.TAG,
      getFn: ({ layer }) =>
        // @ts-ignore
        layer?.details?.display_hints?.search_tags?.join('|'),
    },
    {
      name: FuseOptionKeys.CATEGORY,
      getFn: ({ layer }) => {
        // @ts-ignore
        return getCategoryLabel(layer?.details?.display_hints?.layer_category);
      },
    },
    {
      name: FuseOptionKeys.DESCRIPTION,
      getFn: ({ layer }) => layer?.details?.description,
    },
    {
      name: FuseOptionKeys.COLUMN_NAME,
      getFn: ({ layer }) =>
        layer?.details?.columns.map(({ name }) => name).join('|'),
    },
    {
      name: FuseOptionKeys.COLUMN_KEY,
      getFn: ({ layer }) =>
        layer?.details?.columns.map(({ key }) => key).join('|'),
    },
    {
      name: FuseOptionKeys.COLUMN_DESCRIPTION,
      getFn: ({ layer }) =>
        layer?.details?.columns
          .filter(({ description }) => !!description)
          .map(({ description }) => description)
          .join('|'),
    },
    {
      name: FuseOptionKeys.SOURCE,
      getFn: ({ layer }) => layer?.details?.source?.name,
    },
  ],
};

const DEFAULT_FILTER_STATE = Object.values(FuseOptionKeys).reduce(
  (acc, key) => ({ ...acc, [key]: true }),
  {},
);

export default function LayerManagerSearch() {
  const dispatch = useDispatch();
  const { searchString } = useSelector(selectLayerManager);
  const projectId = useSelector(getActiveProjectId);
  const [debouncedSearchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);
  const [isOpen, setIsOpen] = useState(false);
  const { namespace, key } = parseFullPath(projectId);
  const [editableOptions, setEditableOptions] = useState({
    ignoreLocation: true,
    ignoreFieldNorm: false,
    threshold: 0.15,
    distance: 500,
    minMatchCharLength: 3,
    debounce: 500,
  });
  const { data } = useGetProjectAvailableLayersQuery(
    {
      namespace,
      key,
    },
    { skip: !namespace || !key || !projectId },
  );

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchValue(searchString);
    }, editableOptions.debounce);
    return () => {
      clearTimeout(handler);
    };
  }, [editableOptions.debounce, searchString]);

  const showSearchConfig = useUserFlag('show-search-config');

  const layers = _.sortBy(data?.reference, ({ layer }) => layer?.details?.name);

  // Filter out any layer refs before we start grouping
  const results: FuseResult<SearchResultItem>[] = useMemo(() => {
    if (!layers) {
      return [];
    }
    if (debouncedSearchValue) {
      const filteredKeys = DEFAULT_OPTIONS.keys.filter(
        ({ name }) => filters[name],
      );
      const fuse = new Fuse(layers, {
        ...DEFAULT_OPTIONS,
        ...editableOptions,
        keys: filteredKeys,
      });
      return fuse.search(debouncedSearchValue);
    }
    return [];
  }, [editableOptions, filters, layers, debouncedSearchValue]);

  const handleChange = useCallback(
    (event: any) => {
      const newValue = event.target.value;
      dispatch(searchUpdated(newValue));
    },
    [dispatch],
  );

  const handleClearSearch = () => {
    dispatch(clearSearch(debouncedSearchValue));
  };

  const handleSelect = ({ fullPath, categoryKey }) => {
    dispatch(categoryExpanded(categoryKey));
    // Adds a brief delay to make sure the category is expanded on longer lists
    setTimeout(() => {
      dispatch(selectLayer(fullPath));
    }, 700);
  };

  const handleUpdateOption = name => e => {
    // "Ignore" fields are checkboxes
    if (name.includes('ignore')) {
      setEditableOptions(state => ({ ...state, [name]: e.target.checked }));
    } else {
      setEditableOptions(state => ({ ...state, [name]: e }));
    }
  };

  const handlePopoverInteraction = (open, e) => {
    // Prevent close while focus is maintained on input field
    if (document.activeElement !== e?.target || open === true) {
      setIsOpen(open);
    }
  };

  return (
    <Flex
      alignItems="center"
      style={{ margin: '16px 0', width: '-webkit-fill-available' }}>
      <Popover2
        position={Position.BOTTOM}
        fill
        minimal
        matchTargetWidth
        openOnTargetFocus
        autoFocus={false}
        isOpen={isOpen}
        onInteraction={handlePopoverInteraction}
        targetProps={{
          style: { width: '100%' },
        }}
        content={
          <SearchMenu
            results={results}
            onClick={handleSelect}
            searchValue={debouncedSearchValue}
            minMatchCharLength={editableOptions.minMatchCharLength}
          />
        }>
        <InputGroup
          data-testid="layer-search-input"
          fill
          name="layerName"
          large
          leftIcon="search"
          placeholder={t`Search for layers`}
          autoComplete="off"
          value={searchString}
          onChange={handleChange}
          rightElement={
            <Button
              data-testid="clear-layer-search"
              name="clearSearch"
              minimal
              small
              onClick={handleClearSearch}
              icon="cross"
              disabled={!searchString}
            />
          }
        />
      </Popover2>
      <Popover2
        position={Position.BOTTOM}
        fill
        openOnTargetFocus
        autoFocus={false}
        enforceFocus={false}
        content={
          <Flex flexDirection="col" style={{ padding: 8 }}>
            <H6>Filters</H6>
            {Object.values(FuseOptionKeys).map(filterKey => {
              // Disable if there's only one filter left
              const disabled =
                Object.values(filters).map(filterValue => Boolean(filterValue))
                  .length === 1;
              return (
                <Checkbox
                  data-testid={`${filterKey}-checkbox`}
                  key={filterKey}
                  label={filterKey}
                  disabled={disabled}
                  alignIndicator={Alignment.LEFT}
                  checked={filters[filterKey]}
                  onChange={() =>
                    setFilters(filterState => ({
                      ...filterState,
                      [filterKey]: !filterState[filterKey],
                    }))
                  }
                />
              );
            })}
          </Flex>
        }>
        <Button
          data-testid="filter-menu"
          name="filter-menu"
          minimal
          icon={<Icon path={mdiFilterVariant} size={1} />}
        />
      </Popover2>
      {showSearchConfig && (
        <Popover2
          position={Position.BOTTOM}
          fill
          openOnTargetFocus
          autoFocus={false}
          enforceFocus={false}
          content={
            <ConfigMenu
              onChange={handleUpdateOption}
              options={editableOptions}
            />
          }>
          <Button name="search-config" minimal icon="cog" />
        </Popover2>
      )}
    </Flex>
  );
}

const ConfigMenu = ({ onChange, options }) => {
  const {
    ignoreLocation,
    ignoreFieldNorm,
    threshold,
    distance,
    minMatchCharLength,
    debounce,
  } = options;
  return (
    <Flex flexDirection="col" style={{ padding: 8 }}>
      <Checkbox
        label="Ignore Location"
        alignIndicator={Alignment.RIGHT}
        checked={ignoreLocation}
        onChange={onChange('ignoreLocation')}
      />
      <Checkbox
        label="Ignore Field Norm"
        alignIndicator={Alignment.RIGHT}
        checked={ignoreFieldNorm}
        onChange={onChange('ignoreFieldNorm')}
      />
      <Label>
        Threshold
        <NumericInput
          value={threshold}
          onValueChange={onChange('threshold')}
          max={1}
          min={0}
          stepSize={0.05}
          minorStepSize={0.05}
        />
      </Label>
      <Label>
        Distance
        <NumericInput
          value={distance}
          onValueChange={onChange('distance')}
          min={0}
          stepSize={1}
          disabled={ignoreLocation}
        />
      </Label>
      <Label>
        Minimum Character Length
        <NumericInput
          value={minMatchCharLength}
          onValueChange={onChange('minMatchCharLength')}
          stepSize={1}
        />
      </Label>
      <Label>
        Debounce (ms)
        <NumericInput
          value={debounce}
          onValueChange={onChange('debounce')}
          min={0}
          stepSize={100}
        />
      </Label>
    </Flex>
  );
};
